import React from 'react'
import { Link } from 'react-router-dom'
import {ArrowRight, Wifi, Shield, Train, Building, Ticket, MapPin} from 'lucide-react'

const HomePage = () => {
  const essentials = [
    {
      icon: <Train className="w-6 h-6" />,
      title: 'JR Pass Alternatives',
      description: 'Save money with regional passes and IC cards',
      link: '/article/jr-pass-alternatives',
      cta: 'Compare Prices'
    },
    {
      icon: <Wifi className="w-6 h-6" />,
      title: 'Japan eSIM & WiFi',
      description: 'Stay connected with unlimited data options',
      link: '/article/best-esim-japan',
      cta: 'Check Plans'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Travel Insurance',
      description: 'Comprehensive coverage for your Japan trip',
      link: '/article/travel-insurance-japan',
      cta: 'Get Quote'
    }
  ]

  const trending = [
    {
      image: 'https://images.pexels.com/photos/2187605/pexels-photo-2187605.jpeg?auto=compress&cs=tinysrgb&w=800',
      title: 'Shibuya Sky Sunset Tickets',
      description: 'Book advance tickets to Tokyo\'s newest observation deck. Avoid 2-hour queues.',
      link: '/article/shibuya-sky-tickets',
      badge: 'Selling Out Fast'
    },
    {
      image: 'https://images.pexels.com/photos/6479606/pexels-photo-6479606.jpeg?auto=compress&cs=tinysrgb&w=800',
      title: 'Tokyo DisneySea Fantasy Springs',
      description: 'New expansion opening 2024. Get early-bird ticket pricing before prices rise.',
      link: '/article/disneysea-fantasy-springs',
      badge: 'New Opening'
    }
  ]

  const neighborhoods = [
    {
      image: 'https://images.pexels.com/photos/2614818/pexels-photo-2614818.jpeg?auto=compress&cs=tinysrgb&w=600',
      name: 'Shinjuku',
      description: 'Neon-lit nightlife, luxury hotels, and bustling station hub',
      highlights: ['Best hotels with views', 'Omoide Yokocho food alley', 'Robot Restaurant'],
      link: '/guides/shinjuku'
    },
    {
      image: 'https://images.pexels.com/photos/2070033/pexels-photo-2070033.jpeg?auto=compress&cs=tinysrgb&w=600',
      name: 'Ueno',
      description: 'Museums, cherry blossoms, and family-friendly accommodations',
      highlights: ['Near Tokyo Station', 'Affordable ryokans', 'Ueno Park & Zoo'],
      link: '/guides/ueno'
    },
    {
      image: 'https://images.pexels.com/photos/3370598/pexels-photo-3370598.jpeg?auto=compress&cs=tinysrgb&w=600',
      name: 'Shimokitazawa',
      description: 'Vintage shopping, indie cafes, and bohemian charm',
      highlights: ['Trendy boutique hotels', 'Live music venues', 'Thrift shopping'],
      link: '/guides/shimokitazawa'
    }
  ]

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative h-[600px] bg-gray-900">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/2506923/pexels-photo-2506923.jpeg?auto=compress&cs=tinysrgb&w=1600)',
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="max-w-2xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Book Smarter, Travel Better in Tokyo
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Skip the research overwhelm. Get instant access to the best deals on JR Passes, 
              hotel rooms with Tokyo Tower views, and skip-the-line attraction tickets.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#essentials"
                className="inline-flex items-center justify-center px-8 py-4 bg-red-600 text-white text-lg font-semibold rounded-lg hover:bg-red-700 transition-colors"
              >
                Browse Top Deals
                <ArrowRight className="ml-2" size={20} />
              </a>
              <a
                href="#trending"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                What's Trending
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Top Booking Essentials */}
      <section id="essentials" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Top Booking Essentials
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The three things every Tokyo traveler needs to book before arrival
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {essentials.map((item, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center text-red-600 mb-6">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{item.description}</p>
                <Link
                  to={item.link}
                  className="inline-flex items-center text-red-600 font-semibold hover:text-red-700 transition-colors"
                >
                  {item.cta}
                  <ArrowRight className="ml-2" size={18} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trending Now */}
      <section id="trending" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Trending Now in Tokyo
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Hot tickets selling out fast. Book ahead to secure your spot.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {trending.map((item, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative h-64">
                  <img 
                    src={item.image} 
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute top-4 right-4 px-3 py-1 bg-red-600 text-white text-sm font-semibold rounded">
                    {item.badge}
                  </span>
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{item.description}</p>
                  <Link
                    to={item.link}
                    className="inline-flex items-center justify-center w-full px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Check Availability
                    <ArrowRight className="ml-2" size={18} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Neighborhood Guides */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Where to Stay in Tokyo
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Neighborhood guides to help you choose the perfect location
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {neighborhoods.map((area, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={area.image} 
                    alt={area.name}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center mb-3">
                    <MapPin className="text-red-600 mr-2" size={20} />
                    <h3 className="text-xl font-bold text-gray-900">{area.name}</h3>
                  </div>
                  <p className="text-gray-600 mb-4">{area.description}</p>
                  <ul className="space-y-2 mb-6">
                    {area.highlights.map((highlight, idx) => (
                      <li key={idx} className="text-sm text-gray-700 flex items-center">
                        <span className="w-1.5 h-1.5 bg-red-600 rounded-full mr-2"></span>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={area.link}
                    className="inline-flex items-center text-red-600 font-semibold hover:text-red-700 transition-colors"
                  >
                    Explore {area.name}
                    <ArrowRight className="ml-2" size={18} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Articles Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Most Popular Booking Guides
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'JR Pass Alternatives for Tokyo, Osaka, & Kyoto (2025 Guide)', slug: 'jr-pass-alternatives', category: 'Transport' },
              { title: 'Best eSIM for Japan Travel: Unlimited Data vs. Pocket Wifi', slug: 'best-esim-japan', category: 'Connectivity' },
              { title: '10 Best Tokyo Hotels with a View of Tokyo Tower', slug: 'tokyo-tower-view-hotels', category: 'Accommodation' },
              { title: 'Shibuya Sky Sunset Ticket Reservation Strategy', slug: 'shibuya-sky-tickets', category: 'Attractions' },
              { title: 'TeamLab Planets vs. Borderless: Which is Better?', slug: 'teamlab-comparison', category: 'Experiences' },
              { title: 'Private Sumo Morning Practice Tours in Tokyo', slug: 'sumo-tours', category: 'Tours' }
            ].map((article, index) => (
              <Link
                key={index}
                to={`/article/${article.slug}`}
                className="block bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-red-600"
              >
                <span className="inline-block px-3 py-1 bg-red-100 text-red-600 text-xs font-semibold rounded mb-3">
                  {article.category}
                </span>
                <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                  {article.title}
                </h3>
                <div className="flex items-center text-red-600 font-semibold text-sm">
                  Read Guide
                  <ArrowRight className="ml-2" size={16} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default HomePage
